import { DownloadOutlined, DeleteFilled, PlusOutlined } from '@ant-design/icons';
import { Button, Card, Col, Form, Input, message, Modal, Row, Spin } from 'antd';
import React from 'react';
import ZTable, { IFilterItem, ITableRef, IColumnType } from '@/components/ZTable';
import { useTranslation } from 'react-i18next';
import { ModalWidth, ROW_GUTTER, VALIDATE_REQUIRED } from '@/config';
import { fileService } from '@/services/fileService';
import { BlobStorageContainerType } from '@/constants';
import { BusDocumentDto } from '@/__generated';
import { apiSdk } from '@/httpClient';

type Props = {
  onClose: () => void;
  busId: string;
};

const columns: IColumnType<BusDocumentDto>[] = [
  {
    title: 'busesPage_FileName',
    dataIndex: 'fileName',
    width: 200,
    ellipsis: true,
  },
  {
    title: 'base.Download',
    dataIndex: 'fileUrl',
    width: 150,
    align: 'center',
    render: v => (
      <Button
        type="link"
        icon={<DownloadOutlined />}
        onClick={() => {
          fileService.downloadMedia(v, BlobStorageContainerType.BusContainer);
        }}
      ></Button>
    ),
  },
];

const filters: Array<IFilterItem> = [{ type: 'TEXT', name: 'fileName', label: 'busesPage_FileName', mdSize: 12, xlSize: 12, xxlSize: 12 }];
export default function BusDocumentsModal({ onClose, busId }: Props) {
  const ref = React.useRef<HTMLElement>();
  const { t } = useTranslation();
  const zTb = React.useRef<ITableRef>();
  const [form] = Form.useForm();
  const [loading, setLoading] = React.useState(false);
  const [fileUrl, setFileUrl] = React.useState<string>();
  const onFileChange = (e: any) => {
    if (e.currentTarget?.files && e.currentTarget?.files.length > 0) {
      setLoading(true);
      const file = e.currentTarget.files[0];
      apiSdk.MediaService.uploadDocument({
        container: BlobStorageContainerType.BusContainer,
        file: file,
      })
        .then(response => {
          if (response?.success) {
            setFileUrl(response.url);
          } else {
            if (response.message) {
              message.error(t(response.message || ''));
            }
          }
        })
        .catch(error => {
          message.error(error);
        })
        .finally(() => {
          setLoading(false);
        });
    }
    e.currentTarget.value = '';
  };
  const onFormSumit = (values: any) => {
    if (fileUrl) {
      setLoading(true);
      apiSdk.BusDocumentService.create({
        body: {
          fileName: values.fileName,
          fileUrl: fileUrl,
          busId: busId,
        },
      })
        .then(response => {
          if (response?.success) {
            zTb.current?.reload();
            form.resetFields();
            setFileUrl(undefined);
          } else {
            if (response.message) {
              message.error(t(response.message || ''));
            }
          }
        })
        .catch(error => {
          message.error(error);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };
  return (
    <Modal
      style={{ top: 10 }}
      visible={true}
      maskClosable={false}
      width={ModalWidth.medium}
      className="nta-custom-form"
      title={t('busesPage_UploadDocuments')}
      forceRender={true}
      okText={t('save')}
      cancelText={t('Close')}
      okButtonProps={{ style: { display: 'none' } }}
      onCancel={onClose}
    >
      <Spin spinning={loading}>
        <Card className="mtopo-card" size="small" title={t('busesPage_UploadDocuments')}>
          <Form form={form} layout="vertical" onFinish={onFormSumit}>
            <Row gutter={ROW_GUTTER}>
              <Col md={8}>
                <Form.Item label={t('busesPage_FileName')} name="fileName" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                  <Input />
                </Form.Item>
              </Col>
              <Col md={12}>
                <Form.Item label="   ">
                  {fileUrl ? (
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ flex: 1, padding: '0 10px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {fileUrl}
                        </span>
                        <Button
                          onClick={() => {
                            setFileUrl(undefined);
                          }}
                          icon={<DeleteFilled />}
                          danger
                          type="link"
                          size="small"
                        />
                      </div>
                    </div>
                  ) : (
                    <>
                      <Button
                        onClick={() => {
                          ref.current?.click();
                        }}
                        type="default"
                        icon={<PlusOutlined />}
                      >
                        {t('busesPage_SelectFile')}
                      </Button>
                      <input id="upload" ref={ref as any} type="file" style={{ display: 'none' }} onChange={onFileChange} />
                    </>
                  )}
                </Form.Item>
              </Col>
              <Col md={4}>
                <Form.Item label="  ">
                  <Button type="primary" htmlType="submit" disabled={!fileUrl}>
                    {t('busesPage_Upload')}
                  </Button>
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Card>
      </Spin>

      <ZTable<BusDocumentDto>
        columns={columns}
        primaryKey="id"
        tableRef={zTb}
        defaultFilter={{
          busId: busId,
        }}
        url="/fleets/bus-documents/get-bus-documents"
        bordered
        order_by="createdOn"
        order_by_asc={true}
        allowEdit={false}
        allowDelete={true}
        delete_url="/fleets/bus-documents/delete"
        filters={filters}
        searchBtnAlignLeft
      ></ZTable>
    </Modal>
  );
}
